/**
 * Funções de tracking de pixels com suporte a event_id para dedupe com CAPI
 * Mantém compatibilidade com firePixel.ts existente
 */

import { getOrCreateEventId } from './firePixel';
import { firePixelEvent } from './firePixel';
import { PIXEL_EVENTS } from './pixelConstants';
import publicApi from '../services/publicApi';
import { getFacebookCookies } from './facebookCookies';

type Pixel = {
    id: string;
    provider: string;
    pixelId?: string;
    accessToken?: string;
    domain?: string;
    [key: string]: any;
};

type EventPayload = {
    value?: number | string;
    currency?: string;
    transactionId?: string;
    eventID?: string;
    [key: string]: any;
};

/**
 * Dispara evento PageView com event_id
 * Deve ser disparado no primeiro load e em cada troca de rota (SPA)
 * @param pixels Lista de pixels configurados
 * @param sessionId ID da sessão (opcional)
 */
export const firePageView = (pixels: Pixel[], sessionId?: string) => {
    const eventID = getOrCreateEventId('pageview', sessionId);
    
    // PageView não requer parâmetros adicionais, apenas eventID
    firePixelEvent(pixels, PIXEL_EVENTS.PAGE_VIEW, {
        eventID,
    });
    
    return eventID;
};

/**
 * Dispara evento ViewContent com event_id
 * @param pixels Lista de pixels configurados
 * @param payload Dados do conteúdo visualizado
 * @param sessionId ID da sessão/carrinho
 */
export const fireViewContent = (
    pixels: Pixel[],
    payload: EventPayload,
    sessionId?: string
) => {
    const eventID = getOrCreateEventId('viewcontent', sessionId);
    
    // Garantir parâmetros obrigatórios do Meta Pixel
    const validatedPayload = {
        content_ids: payload.content_ids || ['unknown'],
        content_type: payload.content_type || 'product',
        value: payload.value !== undefined ? payload.value : 0,
        currency: payload.currency || 'BRL',
        ...payload,
        eventID,
    };
    
    firePixelEvent(pixels, PIXEL_EVENTS.VIEW_CONTENT, validatedPayload);
    
    return eventID;
};

/**
 * Dispara evento InitiateCheckout com event_id
 * @param pixels Lista de pixels configurados
 * @param payload Dados do checkout
 * @param sessionId ID da sessão/carrinho
 */
export const fireInitiateCheckout = (
    pixels: Pixel[],
    payload: EventPayload,
    sessionId?: string
) => {
    const eventID = getOrCreateEventId('initcheckout', sessionId);
    
    // Garantir parâmetros obrigatórios do Meta Pixel
    const validatedPayload = {
        content_ids: payload.content_ids || ['unknown'],
        content_type: payload.content_type || 'product',
        value: payload.value !== undefined ? payload.value : 0,
        currency: payload.currency || 'BRL',
        ...payload,
        eventID,
    };
    
    firePixelEvent(pixels, PIXEL_EVENTS.INITIATE_CHECKOUT, validatedPayload);
    
    return eventID;
};

/**
 * Dispara evento AddPaymentInfo com event_id
 * @param pixels Lista de pixels configurados
 * @param payload Dados do pagamento
 * @param sessionId ID da sessão/carrinho
 */
export const fireAddPaymentInfo = (
    pixels: Pixel[],
    payload: EventPayload,
    sessionId?: string
) => {
    const eventID = getOrCreateEventId('addpaymentinfo', sessionId);
    
    // Garantir parâmetros obrigatórios do Meta Pixel
    const validatedPayload = {
        content_ids: payload.content_ids || ['unknown'],
        content_type: payload.content_type || 'product',
        value: payload.value !== undefined ? payload.value : 0,
        currency: payload.currency || 'BRL',
        ...payload,
        eventID,
    };
    
    firePixelEvent(pixels, PIXEL_EVENTS.ADD_PAYMENT_INFO, validatedPayload);
    
    return eventID;
};

/**
 * Dispara evento Purchase com event_id
 * @param pixels Lista de pixels configurados
 * @param payload Dados da compra
 * @param orderId ID do pedido (obrigatório para dedupe)
 */
export const firePurchaseWithEventId = (
    pixels: Pixel[],
    payload: EventPayload,
    orderId: string
) => {
    const eventID = getOrCreateEventId('purchase', orderId);
    
    // Garantir parâmetros obrigatórios do Meta Pixel
    const validatedPayload = {
        content_ids: payload.content_ids || ['unknown'],
        content_type: payload.content_type || 'product',
        value: payload.value !== undefined ? payload.value : 0,
        currency: payload.currency || 'BRL',
        ...payload,
        eventID,
        transactionId: orderId,
    };
    
    firePixelEvent(pixels, PIXEL_EVENTS.PURCHASE, validatedPayload);
    
    return eventID;
};

/**
 * Obtém o event_id atual para um tipo de evento e chave
 * Útil para enviar no header de requisições ao backend
 */
export const getCurrentEventId = (
    kind: 'pageview' | 'viewcontent' | 'initcheckout' | 'addpaymentinfo' | 'purchase',
    key?: string
): string | null => {
    if (!key) return null;
    
    const storageKey = `sellyo:event:${kind}:${key}`;
    
    try {
        return sessionStorage.getItem(storageKey);
    } catch (err) {
        console.warn('[PixelTracking] Erro ao recuperar event_id:', err);
        return null;
    }
};

/**
 * _fbc do clique no anúncio. O cookie só existe se o pixel rodou numa página com `fbclid`
 * na URL; quando o comprador chega no checkout com `?fbclid=` e o cookie ainda não existe,
 * monta no formato da Meta (fb.1.<ms>.<fbclid>). Guardado na sessão para o InitiateCheckout
 * e o pagamento mandarem o MESMO valor.
 */
export const getFbc = (): string | undefined => {
    const fromCookie = getFacebookCookies().fbc;
    if (fromCookie) return fromCookie;
    try {
        const saved = sessionStorage.getItem('sellyo:fbc');
        if (saved) return saved;
        const fbclid = new URLSearchParams(window.location.search).get('fbclid');
        if (!fbclid) return undefined;
        const fbc = `fb.1.${Date.now()}.${fbclid}`;
        sessionStorage.setItem('sellyo:fbc', fbc);
        return fbc;
    } catch {
        return undefined;
    }
};

/**
 * InitiateCheckout ao ABRIR o checkout (antes só disparava no blur do e-mail, e quem
 * desistia antes de digitar não contava).
 *
 * Navegador (fbq) + servidor (API de Conversões) com o MESMO event_id; o servidor só
 * envia para pixel com token e com InitiateCheckout ligado. Uma vez por sessão da aba.
 */
export const trackCheckoutOpened = (
    pixels: Pixel[],
    data: { offerId: string; productId?: string; value: number; currency?: string; sessionId: string },
) => {
    const flag = `sellyo:initcheckout:${data.sessionId}`;
    try {
        if (sessionStorage.getItem(flag)) return;
        sessionStorage.setItem(flag, '1');
    } catch { /* sem storage: dispara mesmo assim, o event_id deduplica */ }

    const eventID = fireInitiateCheckout(pixels, {
        value: data.value,
        currency: data.currency || 'BRL',
        content_ids: [data.productId || data.offerId],
        content_type: 'product',
        num_items: 1,
    }, data.sessionId);

    // Espera o pixel gravar o _fbp (ele nasce no primeiro carregamento do fbevents)
    setTimeout(() => {
        publicApi.post('/api/public/pixel/initiate-checkout', {
            offerId: data.offerId,
            eventId: eventID,
            sourceUrl: window.location.href,
            fbp: getFacebookCookies().fbp,
            fbc: getFbc(),
        }).catch(() => { /* rastreamento nunca atrapalha o checkout */ });
    }, 1500);
};
